import { useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCents } from "@/lib/utils";
import { PaymentScheduleModal } from "@/features/oic/components/payment-schedule-modal";
import type { OICSummary } from "@/features/oic/types";

interface Props {
  clientId: string;
  caseId: string;
  summary: OICSummary | null;
}

/**
 * Step 2 — RCP / Summary of Alternatives. The legacy `ws_offer` three boxes:
 * Installment Agreement (the "consider an IA instead" context, read-only),
 * Offer in Compromise (lump-sum 12-mo vs periodic 24-mo computed amounts +
 * the ability-to-full-pay comparison), and Currently Not Collectible (advisory).
 * Every number is read from the backend summary (Tier 2.2 calc) — nothing here
 * is re-derived. The per-tax-year include/exclude list is owned by the worksheet
 * offer tab (seam link).
 */
export function RcpStep({ clientId, caseId, summary }: Props) {
  const [scheduleOpen, setScheduleOpen] = useState(false);

  if (!summary?.available) {
    return (
      <Alert>
        <AlertTitle>No figures yet</AlertTitle>
        <AlertDescription>
          Recompute on the Financials step once the worksheet is complete.
        </AlertDescription>
      </Alert>
    );
  }

  const money = (c?: number) => (c != null ? formatCents(c) : "—");

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 lg:grid-cols-3">
        {/* --- Offer in Compromise --- */}
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Offer in Compromise
              {summary.fullPayPossible ? (
                <Badge variant="destructive">Full-pay risk</Badge>
              ) : (
                <Badge variant="secondary">Likely viable</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            <Row label="Lump-Sum Cash (12 mo)" value={money(summary.lumpSumOfferCents)} strong />
            <Row label="Periodic (24 mo)" value={money(summary.periodicOfferCents)} strong />
            <hr className="my-1" />
            <Row label="Quick-sale assets" value={money(summary.totalAssetsCents)} />
            <Row label="RMI × CSED months" value={`${money(summary.rmiCents)} × ${summary.csedMonths ?? "—"}`} />
            <Row label="Total tax due" value={money(summary.taxDueCents)} />
            <p className="text-muted-foreground pt-1 text-xs">
              {summary.fullPayPossible
                ? "Collectible over the statute exceeds the debt — the IRS will most likely deny."
                : "Offer is below the collectible amount — a candidate for compromise."}
            </p>
          </CardContent>
        </Card>

        {/* --- Installment Agreement --- */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Installment Agreement
              <Badge variant="outline">{summary.iaTypeLabel || summary.iaType}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            <Row label="Type" value={`${summary.iaType} (${summary.iaCondition ?? ""})`} />
            <Row label="Monthly payment" value={money(summary.iaMonthlyPaymentCents)} strong />
            <Row label="Months" value={String(summary.iaCalculatedMonths ?? "—")} />
            <Row label="Interest rate" value={summary.interestRate ? `${summary.interestRate}%` : "—"} />
            <Button variant="outline" size="sm" className="mt-1" onClick={() => setScheduleOpen(true)}>
              View payment schedule
            </Button>
          </CardContent>
        </Card>

        {/* --- Currently Not Collectible --- */}
        <Card>
          <CardHeader>
            <CardTitle>Currently Not Collectible</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {summary.cncLikely ? (
              <p>
                RMI is at or below the CNC threshold — the client{" "}
                <span className="font-medium">may qualify for CNC status</span>. Advisory
                only; the CNC workflow is out of scope here.
              </p>
            ) : (
              <p className="text-muted-foreground">
                RMI is above the CNC threshold — not a likely CNC candidate.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/client/${clientId}/cases/${caseId}/worksheets/offer`}>
            <FiExternalLink className="size-4" /> Adjust included tax years on the worksheet
          </Link>
        </Button>
      </div>

      <PaymentScheduleModal caseId={caseId} open={scheduleOpen} onOpenChange={setScheduleOpen} />
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? "font-semibold tabular-nums" : "tabular-nums"}>{value}</span>
    </div>
  );
}
